/**
 * AddItemCascade — Slideover wizard for adding line items to a quote.
 *
 * PR-6 rebuild (2026-07-25): step 3 is now a per-sub-area COMPOSITE builder
 * (see `AddItemGroups.js`). Old category/product/qty screens are deleted for
 * the multi-sub-area path.
 *
 * Steps (new order):
 *   0. Area
 *   1. Sub-area (multi-select) — skipped if area has none
 *   2. Composite group builder (AddItemGroups): category → product →
 *      measurement + extras + accessories, one group per sub-area.
 *
 * The old separate "Custom Item" branch is preserved as an alt path at step 2
 * (single-item flow using buildCustomLineItem).
 *
 * Stepper is 3 dots with sub-areas, 2 dots without (R15).
 *
 * PR-9 (2026-07-25): step 1 (sub-area multi-select) now renders BIDIT sub-areas
 * PLUS tenant custom sub-areas (from settings/sub_areas via mergeAreasWithTenant).
 * The inline "custom sub-area" Input + Add button that used to live in this
 * step has been removed — custom sub-areas are added in Settings → Quote Areas,
 * not minted inside the wizard. Governance: the wizard is a READ surface for
 * areas/sub-areas; it can no longer create either. See operator ruling 2026-07-21.
 */

import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Alert } from '../../ui/Alert';
import Slideover from '../../ui/Slideover';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import SelectMenu from '../../ui/SelectMenu';
import {
  buildLineItem,
  buildCustomLineItem,
} from '../../../quote-builder/seedCatalog';
import { mergeAreasWithTenant, relevantBuiltInAreas } from '../../../quote-builder/areasModel';
import { applyRoundMaterials } from '../../../quote-builder/roundMaterials';
import { VOCAB_SEEDS } from '../../../constants/vocabSeeds';
import { settingsAPI } from '../../../services/api';
import AddItemGroups from './AddItemGroups';
import AccessoryPicker from './AccessoryPicker';
import AddAlternateModal from './modals/AddAlternateModal';
// PR-65 Q-04: collectOptionGroupIds/deriveOptionLabel imports removed with
// the Group droplist. If a future PR reintroduces group selection here, the
// import can come back — the helpers still live at quote-builder/optionLabel.js.
import { parseNullableMarginPct, clampMarginPct, marginPctError } from '../../../quote-builder/marginEngine';

/**
 * IDI-339: Resolve a line item's sub-area ID for edit mode.
 *
 * Migrated quotes get their line items from `flattenAreaLineItems`
 * (quotePrefill.js), which emits `subAreaLabel` but NOT `subAreaId`. Without
 * an id, the edit-mode seed sites below (`subAreaIds` state + `initialEditItem`)
 * can't find the target group in AddItemGroups' reducer, so the edit slideover
 * opens blank. When the id is absent, fall back to matching the line's
 * `subAreaLabel` (case-insensitive) against the tenant-aware merged sub-area
 * list, which already carries the display labels. Returns null when nothing
 * matches (renamed/removed sub-area) — caller decides how to degrade.
 *
 * @param {object} item          the line item being edited
 * @param {Array}  mergedSubAreas [{ id, label }] for the item's area
 * @returns {string|null}
 */
function resolveSubAreaIdFromItem(item, mergedSubAreas) {
  if (!item) return null;
  if (item.subAreaId) return item.subAreaId;
  const label = (item.subAreaLabel || '').trim().toLowerCase();
  if (!label || !Array.isArray(mergedSubAreas)) return null;
  const match = mergedSubAreas.find(
    (s) => (s.label || '').trim().toLowerCase() === label
  );
  return match ? match.id : null;
}

/**
 * IDI-448: id of the edit-only group for a label-only migrated line whose
 * sub-area no longer exists anywhere. Never persisted: buildLineFromDraft
 * maps it back to "no sub-area id", so the line keeps only its stored label.
 */
const STORED_SUB_AREA_ID = '__stored_sub_area__';

/**
 * IDI-405 D3: a line whose sub-area is no longer offered (a built-in hidden
 * for a migrated dealer, or any id missing from the merged list) still opens
 * filled in Edit under its STORED name. Returns a synthetic `{id, label}` for
 * the edit-only group set, or null when the merged list already has it. Never
 * fed to the Step 2 picker, so new lines can't pick it.
 *
 * IDI-448: `allSubAreas` is the same area's include-inactive list (lookup
 * only). The result carries `editStatus`:
 *   - 'inactive': the dealer made the sub-area inactive (found inactive).
 *   - 'missing':  the sub-area no longer exists at all.
 *   - 'hidden':   an IDI-405 hidden built-in; opens as before, no notice.
 */
function hiddenSubAreaForEdit(item, areaId, mergedSubAreas, allSubAreas) {
  if (!item || !areaId) return null;
  const subs = Array.isArray(mergedSubAreas) ? mergedSubAreas : [];
  const all = Array.isArray(allSubAreas) ? allSubAreas : [];
  const label = (item.subAreaLabel || '').trim();
  const lc = label.toLowerCase();
  const sameLabel = (s) => (s.label || '').trim().toLowerCase() === lc;
  if (item.subAreaId ? subs.some((s) => s.id === item.subAreaId) : (!lc || subs.some(sameLabel))) {
    return null;
  }
  // By id first; a line stored under a legacy id that the merge now shows
  // under its built-in id (IDI-381 name-match) is found by its label.
  const known =
    (item.subAreaId ? all.find((s) => s.id === item.subAreaId) : null) ||
    (lc ? all.find(sameLabel) : null);
  const seedArea = (VOCAB_SEEDS.area?.values || []).find((a) => a.value === areaId);
  const seed = (seedArea?.subAreas || []).find((s) =>
    item.subAreaId ? s.value === item.subAreaId : sameLabel(s)
  );
  let editStatus = 'hidden';
  if (known && known.active === false) editStatus = 'inactive';
  else if (!known && !seed) editStatus = 'missing';
  const id = item.subAreaId || known?.id || seed?.value || (lc ? STORED_SUB_AREA_ID : null);
  if (!id) return null;
  return {
    id,
    label: label || known?.label || seed?.label || id,
    source: known?.source || 'bidit',
    hiddenForEdit: true,
    editStatus,
  };
}

export default function AddItemCascade({
  // IDI-459: when false, the Options area is not offered at all (E-30).
  // Change orders are diff-only, so optional add-ons don't apply.
  allowOptions = true,
  // IDI-823 (D2): the document the sheet adds to. The quote passes nothing
  // and keeps "Add to quote"; a change order passes 'change order'.
  targetNoun = 'quote',
  // IDI-823 (D3, E-31): alternates are a quote concept. When false, no
  // "Add alternative" is offered on saved lines and the modal never mounts.
  // Suppressed by prop, never CSS (09-10), like allowOptions above.
  allowAlternates = true,
  open,
  onClose,
  onAddItem,
  defaultAreaId,
  // PR-6: accessory-catalog integration piped from QuoteBuilderPage so the
  // per-line AccessoryPicker inside the wizard works the same as EditItemModal's.
  accessoryCatalog = [],
  onCreateCustomAccessory,
  // PR-62 (2026-08-19) — the quote's current line items. Used to enumerate
  // existing option groups so the item form can present a Group selector
  // ("New group" | "Option 1" | "Option 2" | ...). One-way prop; the wizard
  // never mutates it. Empty array is safe (fresh quote → no existing groups).
  lineItems = [],
  // IDI-178: quote-level Round Materials toggle. Applied to entered sqft
  // in buildLineFromDraft before it reaches buildLineItem. See
  // quote-builder/roundMaterials.js for the full placeholder-rule rationale.
  roundMaterials = false,
  // PT-30 (2026-07-27, follow-up to 30.2 ruling): the quote's full margin
  // context, threaded in from QuoteBuilderPage. Passed unmodified to
  // `<AddItemGroups>` so the wizard's committed-line preview cards resolve
  // customer + customer-type levels and match the Estimate Panel's totals.
  // Optional — when absent the engine falls back to `line.overallMargin`.
  marginContext,
  // §4b: tenant's category_margins from Settings → Margins & Labor.
  // Passed through to buildLineItem so the line's categoryMargin is tenant-sourced.
  tenantCategoryMargins,
  // B5: tenant's labor_rates from Settings → Margins & Labor.
  tenantLaborDefaults,
  // PR-66 s2c (Q-11): tenant Quote Items (insulation subset) from
  // settings.quote_items. Merged into Category / Product droplists and
  // threaded into buildLineItem so tenant-authored SKUs price correctly.
  // Optional — empty array is safe (falls back to static catalog).
  tenantQuoteItems = [],
  // IDI-365: true while the parent's tenant catalog is still loading. A line
  // can't be priced from a tenant product until it lands, so "Add to quote"
  // waits instead of committing a line that would fail to resolve.
  catalogLoading = false,
  // IDI-176: wizard-as-editor mode.
  //
  //   mode === 'add'   → default. Wizard opens on step 0 (area picker),
  //                       cascades through sub-areas, category, product,
  //                       and commits new lines via `onAddItem`.
  //
  //   mode === 'edit'  → wizard opens directly on step 2 (composite/items)
  //                       hydrated from `initialItem`. Same fields the Add
  //                       flow exposes (Area, Sub-Area, Product, Sq Ft, Note,
  //                       Additional Labor, Additional Cost, Accessories),
  //                       none of the retired ones (Margin %, Line Total).
  //                       On commit, calls `onEditItem(id, updates)` instead
  //                       of `onAddItem(newLine)`.
  //
  // BACKWARD navigation from step 2 is allowed in edit mode (user can change
  // Area / Sub-Area / Category) — per operator ruling E2 this turn, Product
  // changes are allowed but only to same-Category alternatives, since a
  // Category swap would blow the default-margin semantics of the row.
  //
  // Failure mode attached: mode === 'edit' with a null/missing initialItem
  // silently degrades to add-mode (no crash). The parent must not open
  // the wizard in edit-mode without an initialItem — checked in the callers.
  mode = 'add',
  initialItem = null,
  onEditItem = null,
}) {
  // IDI-176 · edit-mode gate. `isEditing` is `true` iff both mode==='edit'
  // AND initialItem is truthy. Anything else (missing prop, null item) is
  // add-mode with all defaults. This is the belt-and-suspenders for the
  // failure mode called out in the prop docblock above.
  const isEditing = mode === 'edit' && !!initialItem && !!initialItem.id;

  // Selection state (only up through sub-area).
  // IDI-176: in edit mode, jump directly to step 2 (composite/items) with
  // areaId/subAreaIds hydrated from the initialItem. In add mode, the
  // existing behaviour is preserved exactly (step 0, empty selections).
  const [step, setStep] = useState(isEditing ? 2 : 0);
  const [areaId, setAreaId] = useState(
    isEditing ? (initialItem.areaId || defaultAreaId || null) : (defaultAreaId || null)
  );
  const [subAreaIds, setSubAreaIds] = useState(
    isEditing && initialItem.subAreaId ? [initialItem.subAreaId] : []
  );

  // PR-9 (2026-07-25): tenant custom sub-areas from settings/sub_areas, fetched
  // once when the wizard opens and merged with the canonical vocabSeeds.area set
  // to build the multi-select list in step 1. Refetched on each `open` so the
  // wizard picks up sub-areas added in Settings without needing a page reload.
  //
  // PR-67a (2026-08-22): five-state discriminated union replaces the bare
  // `catch { setTenantSubAreas([]) }` swallow. The old shape collapsed
  // LOADING / EMPTY / ERROR / NOT-PERMITTED into a single "empty list"
  // signal, which fed a step-skip heuristic in `selectArea` and produced
  // silent misroutes when a 5xx or 403 hit the settings API. The five
  // states below are rendered explicitly in `renderSubAreaStep` and
  // gate the step-skip decision on POPULATED-with-zero-rows only.
  //
  // State shape:
  //   { kind: 'LOADING' }
  //   { kind: 'POPULATED', rows }  // rows may be empty; that's EMPTY-populated
  //   { kind: 'EMPTY' }             // 200 + empty array (semantically same as POPULATED with [])
  //   { kind: 'ERROR', error }      // network / 5xx
  //   { kind: 'NOT_PERMITTED' }     // 403
  const [subAreasFetch, setSubAreasFetch] = useState({ kind: 'LOADING' });
  // Back-compat derived value: the rest of the file (memos, selectArea) reads
  // tenantSubAreas as a flat array. When the fetch is not POPULATED, treat
  // it as [] for merge purposes — the render forks handle the user-facing
  // message. Merge cannot know whether an empty list means "tenant added
  // nothing" or "the fetch failed", so keeping the array reads at [] is
  // consistent with today's behavior for those code paths.
  const tenantSubAreas = useMemo(
    () => (subAreasFetch.kind === 'POPULATED' ? subAreasFetch.rows : []),
    [subAreasFetch]
  );
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSubAreasFetch({ kind: 'LOADING' });
    (async () => {
      try {
        const items = await settingsAPI.getList('sub_areas');
        if (cancelled) return;
        const rows = Array.isArray(items) ? items : [];
        setSubAreasFetch(
          rows.length === 0 ? { kind: 'EMPTY' } : { kind: 'POPULATED', rows }
        );
      } catch (error) {
        if (cancelled) return;
        const status = error && (error.status || error.statusCode);
        if (status === 403) {
          setSubAreasFetch({ kind: 'NOT_PERMITTED' });
        } else {
          setSubAreasFetch({ kind: 'ERROR', error });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Retry handler for the ERROR fork — re-fires the fetch on demand.
  const retrySubAreasFetch = useCallback(() => {
    let cancelled = false;
    setSubAreasFetch({ kind: 'LOADING' });
    (async () => {
      try {
        const items = await settingsAPI.getList('sub_areas');
        if (cancelled) return;
        const rows = Array.isArray(items) ? items : [];
        setSubAreasFetch(
          rows.length === 0 ? { kind: 'EMPTY' } : { kind: 'POPULATED', rows }
        );
      } catch (error) {
        if (cancelled) return;
        const status = error && (error.status || error.statusCode);
        if (status === 403) {
          setSubAreasFetch({ kind: 'NOT_PERMITTED' });
        } else {
          setSubAreasFetch({ kind: 'ERROR', error });
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // PR-67a (2026-08-22): tenant custom_areas overrides, fetched in parallel
  // with sub_areas. Two roles when merged:
  //   1) row.id matches a BIDIT seed area -> label override (rename).
  //   2) row.id is not a seed id -> new top-level tenant area.
  // Best-effort: a failed fetch degrades to "no overrides" (renamed BIDIT
  // areas show their default label in the wizard). Not fatal.
  const [customAreas, setCustomAreas] = useState([]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const items = await settingsAPI.getList('custom_areas');
        if (!cancelled) setCustomAreas(Array.isArray(items) ? items : []);
      } catch {
        if (!cancelled) setCustomAreas([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // IDI-405 (reopened): PR #462's blanket suppression hid built-in areas
  // that have sub-areas under them. Smarter guard: keep a built-in area
  // if ANY sub-area is parented to it or a custom_areas row overrides it.
  // Built-ins with no tenant usage are suppressed.
  // IDI-662 D1: only for MIGRATED dealers (legacy-origin sub-areas); a
  // clean dealer keeps every built-in. Same shared guard as QuoteAreasPage.
  const subAreasLoading = subAreasFetch.kind === 'LOADING';
  const baseAreas = useMemo(
    () => relevantBuiltInAreas(VOCAB_SEEDS.area?.values || [], tenantSubAreas, customAreas, { subAreasLoading }),
    [customAreas, tenantSubAreas, subAreasLoading]
  );

  // PR-65 Q-13 (2026-08-20, 386_ (2026-08-20, 386_ §1.2): on-open sync when a defaultAreaId is §1.2): on-open sync when a defaultAreaId is
  // supplied by the parent (per-AreaCard "+ Add Item" at QuoteBuilderPage.js:
  // 1407). Two defects this addresses:
  //   1  STALE INITIAL STATE — the useState at :76 seeds `areaId` from
  //      `defaultAreaId` at mount ONLY. AddItemCascade stays mounted across
  //      opens (shown/hidden by the `open` prop), so a later defaultAreaId
  //      change never reached areaId. Open from Area A → close → open from
  //      Area B → sheet stayed on A.
  //   2  STEP ALWAYS 0 — even when the area was known, the user landed on
  //      the area picker.
  //
  // Mirror selectArea's step-jump exactly (:172-179): merged sub-areas empty
  // → step 2 (composite builder), sub-areas exist → step 1 (multi-select).
  // Do NOT hardcode step 1 — an area with no sub-areas must not land the
  // user on an empty picker.
  //
  // Fires only on the false→true transition of `open` so mid-interaction
  // parent re-renders don't yank the user.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;
    // IDI-311 (2026-09-10): the edit-mode seed (isEditing ? 2 : 0 at :118, and
    // the two useState initializers above it) only runs at MOUNT. This
    // component stays mounted across open/close cycles — the SAME bug this
    // Q-13 effect was built to fix for defaultAreaId, but this effect's body
    // only handled the add-mode case. Editing item A then item B (without an
    // intervening remount) left the sheet on item A's step/areaId/subAreaIds,
    // titled "Edit Line Item" with add-mode's step-0 area picker showing.
    // Handle edit mode FIRST and return — the two paths are mutually
    // exclusive (isEditing implies defaultAreaId is not the relevant seed).
    //
    // BOUNDED LIMITATION (2026-09-10, second review): this guard keys on
    // the false→true transition of `open`. If the edit TARGET changed
    // while `open` stayed true — i.e. the parent swapped `initialItem`
    // without a close/reopen cycle — this effect would NOT re-fire and the
    // stale state would persist. Checked reachability at review time:
    // `setEditingItem` (QuoteBuilderPage.js) has three call sites — the
    // line-list's onClick (behind the closed sheet), `onClose` (sets null),
    // and post-save (sets null). The line list is not reachable while the
    // sheet is open, so a same-open item swap cannot happen today. If the
    // line list ever becomes clickable behind an open edit sheet, this
    // effect needs a second guard keyed on `initialItem?.id` changing, not
    // just `open`'s transition. Not coded around pre-emptively — there is
    // no reachable case to test against yet.
    if (isEditing) {
      setStep(2);
      setAreaId(initialItem.areaId || defaultAreaId || null);
      // IDI-339: migrated lines carry `subAreaLabel` but no `subAreaId`.
      // Resolve the id from the label against the tenant-aware merged
      // sub-area list for the line's area so the target group exists; a
      // bare `initialItem.subAreaId` seed would be [] for migrated lines
      // and AddItemGroups would render zero groups (blank dialog).
      const editAreaId = initialItem.areaId || defaultAreaId || null;
      const editMergedSubAreas = editAreaId
        ? (mergeAreasWithTenant(
            baseAreas,
            tenantSubAreas,
            customAreas
          ).find((a) => a.id === editAreaId)?.subAreas || [])
        : [];
      // IDI-448: look the line's sub-area up in the include-inactive list
      // too (lookup only -- the picker keeps the default list).
      const editAllSubAreas = editAreaId
        ? (mergeAreasWithTenant(baseAreas, tenantSubAreas, customAreas, { includeInactive: true })
            .find((a) => a.id === editAreaId)?.subAreas || [])
        : [];
      const editHidden = hiddenSubAreaForEdit(initialItem, editAreaId, editMergedSubAreas, editAllSubAreas);
      const editSubAreaId = resolveSubAreaIdFromItem(
        initialItem,
        editHidden ? [...editMergedSubAreas, editHidden] : editMergedSubAreas
      );
      setSubAreaIds(editSubAreaId ? [editSubAreaId] : []);
      // IDI-311 follow-up (2026-09-10, operator review): reset the
      // custom-item / option-group state too, so switching from editing
      // item A (a custom item, or one in an option group) to editing item
      // B does not carry A's flags into B's session.
      //
      // CORRECTED (2026-09-10, second review): these three were never
      // seeded from `initialItem` even on first mount, and — measured
      // directly against addItemGroupsState.js's INIT_FOR_EDIT case
      // (:277-320) — NOTHING re-derives them. INIT_FOR_EDIT only writes
      // `groups` and `activeDraft`; it has zero references to
      // isCustomItem, selectedOptionGroupId, or optionLineMargin. This
      // reset is the ONLY place these three get corrected between edit
      // sessions — it is load-bearing, not defensive redundancy. An
      // earlier version of this comment claimed INIT_FOR_EDIT handled it;
      // that claim was never checked before being written and was wrong.
      setIsCustomItem(false);
      // IDI-328: seed selectedOptionGroupId from the line being edited's
      // OWN optionId when it's an options-area line, instead of always
      // resetting to '__new__'. Without this, saving an edited options
      // line always minted a fresh optionId (see the commit paths' `'__new__'
      // -> mint new` branch below), detaching the line from whatever
      // Option N group it actually belonged to and creating a brand-new
      // one-line group instead of updating it in place.
      setSelectedOptionGroupId(
        initialItem.areaId === 'options' && initialItem.optionId
          ? initialItem.optionId
          : '__new__'
      );
      setOptionLineMargin(
        initialItem.areaId === 'options' && initialItem.lineMargin != null
          ? String(Math.round(Number(initialItem.lineMargin) * 100))
          : ''
      );
      return;
    }
    if (!defaultAreaId) return; // page-level CTA (defaultAreaId=null) → keep step 0
    setAreaId(defaultAreaId);
    const merged = mergeAreasWithTenant(baseAreas, tenantSubAreas, customAreas);
    const areaObj = merged.find((a) => a.id === defaultAreaId);
    setStep(areaObj && areaObj.subAreas.length === 0 ? 2 : 1);
    // Reset per-commit option state for the fresh flow, matching selectArea.
    setSelectedOptionGroupId('__new__');
    setOptionLineMargin('');
  }, [open, defaultAreaId, baseAreas, tenantSubAreas, customAreas, isEditing, initialItem]);

  // Merged sub-areas for the CURRENT areaId (BIDIT + tenant). Used by the step-1
  // render and by hasSubAreas below. Falls back to [] when areaId is null.
  const mergedSubAreasForArea = useMemo(() => {
    if (!areaId) return [];
    const merged = mergeAreasWithTenant(baseAreas, tenantSubAreas, customAreas);
    return merged.find((a) => a.id === areaId)?.subAreas || [];
  }, [areaId, baseAreas, tenantSubAreas, customAreas]);

  // IDI-448: the same area's sub-areas INCLUDING inactive ones (renamed
  // labels applied). Lookup only, for the Edit path's label/status -- never
  // offered in the Step 1 picker.
  const allAreasForCascade = useMemo(
    () => mergeAreasWithTenant(baseAreas, tenantSubAreas, customAreas, { includeInactive: true }),
    [baseAreas, tenantSubAreas, customAreas]
  );
  const allSubAreasForArea = useMemo(() => {
    if (!areaId) return [];
    return allAreasForCascade.find((a) => a.id === areaId)?.subAreas || [];
  }, [areaId, allAreasForCascade]);

  // IDI-662 D2: Edit on a line whose AREA is inactive (a built-in or custom
  // area the dealer made inactive). The default merge drops the area, so the
  // edit is built from the line's STORED sub-area instead: one group, same
  // id the line had before the area lists landed (IDI-365 draft reset), the
  // stored names, and a notice -- no picker. An area that matches nothing
  // (not even inactive) keeps today's path.
  const editInactiveArea = useMemo(() => {
    if (!isEditing || !areaId) return null;
    if (mergeAreasWithTenant(baseAreas, tenantSubAreas, customAreas).some((a) => a.id === areaId)) return null;
    const found = allAreasForCascade.find((a) => a.id === areaId);
    return found && found.active === false ? found : null;
  }, [isEditing, areaId, baseAreas, tenantSubAreas, customAreas, allAreasForCascade]);
  const editInactiveGroup = useMemo(() => {
    if (!editInactiveArea) return null;
    const storedId =
      initialItem.subAreaId ||
      resolveSubAreaIdFromItem(initialItem, allSubAreasForArea) ||
      (initialItem.subAreaLabel ? STORED_SUB_AREA_ID : null);
    const known = storedId ? allSubAreasForArea.find((s) => s.id === storedId) : null;
    return {
      id: storedId,
      label: initialItem.subAreaLabel || known?.label || initialItem.areaLabel || editInactiveArea.label,
      config: known || null,
      areaLabel: initialItem.areaLabel || editInactiveArea.label,
      subAreaLabel: initialItem.subAreaLabel || known?.label || null,
    };
  }, [editInactiveArea, initialItem, allSubAreasForArea]);

  // IDI-405 D3: edit-only sub-area list. Adds the edited line's hidden
  // sub-area (stored name) so its group card and draft render; the Step 2
  // picker keeps reading mergedSubAreasForArea, so it is never offered.
  const editHiddenSubArea = useMemo(() => {
    // An area with no sub-areas renders one synthetic group (see
    // groupsSubAreas), so there is nothing to add.
    if (!isEditing || mergedSubAreasForArea.length === 0) return null;
    return hiddenSubAreaForEdit(initialItem, areaId, mergedSubAreasForArea, allSubAreasForArea);
  }, [isEditing, initialItem, areaId, mergedSubAreasForArea, allSubAreasForArea]);
  const subAreasForGroups = useMemo(
    () => (editHiddenSubArea ? [...mergedSubAreasForArea, editHiddenSubArea] : mergedSubAreasForArea),
    [editHiddenSubArea, mergedSubAreasForArea]
  );

  // IDI-448: when the edited line's sub-area is inactive or gone, the user
  // may move the line to another (offered) sub-area. null = keep its own.
  const [editSubAreaPick, setEditSubAreaPick] = useState(null);
  useEffect(() => {
    setEditSubAreaPick(null);
  }, [open, initialItem?.id]);
  const editSubAreaNotice =
    editHiddenSubArea && (editHiddenSubArea.editStatus === 'inactive' || editHiddenSubArea.editStatus === 'missing')
      ? editHiddenSubArea
      : null;
  const pickEditSubArea = useCallback((nextId) => {
    if (!nextId) return;
    setEditSubAreaPick(nextId);
    setSubAreaIds([nextId]);
  }, []);

  // PR-67a: merged top-level areas (BIDIT + tenant custom_areas overrides +
  // new tenant top-level areas). Replaces static `AREAS.map` in the step-0
  // picker so a renamed BIDIT area shows the tenant's label and a
  // tenant-created custom area shows up in the wizard at all.
  const mergedAreasForCascade = useMemo(
    () => mergeAreasWithTenant(baseAreas, tenantSubAreas, customAreas),
    [baseAreas, tenantSubAreas, customAreas]
  );

  // IDI-334 (2026-09-12): hide area tiles with zero sub-areas from the
  // step-0 picker grid, instead of rendering them disabled+greyed. Filters
  // `mergedAreasForCascade` — the TENANT-AWARE merged set — not
  // `VOCAB_SEEDS` directly. This matters: several seed areas ship with
  // `subAreas: []` (Basement, Air Sealing, Vent, Removal, Options —
  // vocabSeeds.js:386-390) and only gain sub-areas once a tenant adds
  // custom ones via `tenantSubAreas`/`customAreas`. A filter against the
  // static seeds would hide/show tiles based on what BIDIT ships, not what
  // THIS tenant has configured — a tenant who added sub-areas to "Vent"
  // would still see it hidden, because the seed's own subAreas array is
  // permanently empty regardless of tenant config. Filtering the merged
  // set means a tile reappears the moment a tenant adds a sub-area to it,
  // with no code change and no re-deploy.
  //
  // EXEMPTION: 'options' is deliberately NOT filtered even though its seed
  // (and, in practice, its merged form for almost every tenant) has zero
  // sub-areas. Confirmed by reading selectArea (:392-396) and groupsSubAreas
  // (:414-420): Options is designed around having no sub-areas — selecting
  // it jumps straight to the step-2 composite builder, which synthesizes a
  // single group using the area itself as the sub-area (R15). It can NEVER
  // be "restored by tenant config" the way Basement/Air Sealing/Vent/Removal
  // can, because nothing in this flow ever writes a sub-area under it — so
  // hiding it here would remove a working, permanently-zero-sub-area entry
  // point with no path back. (The render-time comment at the grid map below
  // used to make a different, incorrect claim about WHY Options is reachable
  // — corrected there, at the render site, rather than duplicated here.)
  //
  // IDI-459: `allowOptions` lets a consumer suppress the Options area
  // entirely. A change order is a DIFF of an existing job (ruling 08-21), so
  // offering optional add-ons has no meaning there (E-30) — and suppression
  // is by explicit prop, never a CSS hide or a fork (ruling 09-10). The quote
  // builder passes nothing and keeps the exemption above.
  const visibleAreasForCascade = useMemo(
    () => mergedAreasForCascade.filter((area) => (
      area.id === 'options' ? allowOptions : area.subAreas.length > 0
    )),
    [mergedAreasForCascade, allowOptions]
  );

  // Custom-item single-line branch (alt path from step 2).
  const [isCustomItem, setIsCustomItem] = useState(false);
  // PR-62 (2026-08-19, replaces PT-15 #2 OptionForm interstitial):
  //   • selectedOptionGroupId — which group the item(s) being added belong to.
  //     Sentinel '__new__' → mint a fresh optionId on commit. An existing
  //     optionId from `lineItems` → reuse it (add to existing Option N).
  //     null → not in the Options area, no group tagging happens.
  //   • optionLineMargin — per-line margin % (0..99), applied to every line
  //     in this commit when set (§3ak: options can have a custom margin).
  //     Stored as string for input control; parsed on commit.
  // These replace the removed `optionMeta` state that used to hold
  // {name, description, marginPercent} from the OptionForm interstitial
  // (deleted per 361_ §1 Q2 — group is not authored).
  const [selectedOptionGroupId, setSelectedOptionGroupId] = useState('__new__');
  const [optionLineMargin, setOptionLineMargin] = useState('');

  // IDI-420: a margin of 100% or more is not representable -- lineTotal divides
  // by (1 - margin), so >= 1 throws a RangeError. The `max="99"` attribute above
  // is only a browser hint: typing 200 (or pasting it) still lands in state, and
  // the throw then surfaced from computeQuote during a LATER render, crashing the
  // whole route with "Something went wrong" instead of flagging the field.
  // Surface it at the field, and refuse to commit (see the guards in
  // commitGroups / submitCustomItem).
  const optionMarginError = useMemo(() => marginPctError(optionLineMargin), [optionLineMargin]);
  // PR-06 FIX (2026-07-22): when a user selects multiple sub-areas and then
  // switches to "custom item", the previous implementation fanned the same
  // custom line into EVERY selected sub-area (double/triple add). Custom
  // items now commit to ONE sub-area only; the picker below defaults to the
  // first selected sub-area, and the user can change it inside the form.
  // PR-62: `alias` — per-line customer-facing display name override (§3aj).
  const [customData, setCustomData] = useState({
    description: '', materialCost: '', laborCost: '', margin: '', alias: '', subAreaId: null,
  });
  // IDI-420 (reopened 09-29): the custom item's own Margin % had only the
  // browser hint max="99", so 100+ reached lineTotal's RangeError through this
  // second field. Same rule as the Options field.
  const customMarginError = useMemo(() => marginPctError(customData.margin), [customData.margin]);

  // Dirty-tracking flag surfaced by AddItemGroups.
  const [groupsDirty, setGroupsDirty] = useState(false);

  // Sibling-Slideover contexts (avoid nesting slideovers inside AddItemCascade).
  // When set, the corresponding modal renders as a peer to this Slideover.
  const [accessoryCtx, setAccessoryCtx] = useState(null); // { subAreaId, productId, categoryId, accessories }
  const [altCtx, setAltCtx] = useState(null);             // { subAreaId, lineId, parentLine }

  const groupsRef = useRef(null);

  // ─── Reset on close ──────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    setStep(0);
    setAreaId(defaultAreaId || null);
    setSubAreaIds([]);
    setIsCustomItem(false);
    setCustomData({ description: '', materialCost: '', laborCost: '', margin: '', alias: '', subAreaId: null });
    setAccessoryCtx(null);
    setAltCtx(null);
    setGroupsDirty(false);
    // PR-62 (2026-08-19): reset per-commit option state on close.
    setSelectedOptionGroupId('__new__');
    setOptionLineMargin('');
    onClose();
  }, [defaultAreaId, onClose]);

  // ─── Step navigation ─────────────────────────────────────────────────────
  const hasSubAreas = useMemo(() => mergedSubAreasForArea.length > 0, [mergedSubAreasForArea]);
  const totalSteps = hasSubAreas ? 3 : 2;
  const stepIndex = step; // 0..totalSteps-1

  const selectArea = useCallback((nextAreaId) => {
    setAreaId(nextAreaId);
    setSubAreaIds([]);
    // PR-62 (replaces PT-15 #2 optionMeta reset): reset per-commit option
    // state on every area change. Switching AWAY from options wipes any
    // half-entered group choice / margin so the next commit starts clean.
    setSelectedOptionGroupId('__new__');
    setOptionLineMargin('');
    // GP-03: check MERGED areas (includes tenant custom sub-areas) not just
    // static AREAS. Skip sub-area step only when the merged set is empty.
    const merged = mergeAreasWithTenant(baseAreas, tenantSubAreas, customAreas);
    const areaObj = merged.find((a) => a.id === nextAreaId);
    if (areaObj && areaObj.subAreas.length === 0) {
      setStep(2); // jump straight to composite builder (Options included — N-3)
    } else {
      setStep(1);
    }
  }, [baseAreas, tenantSubAreas, customAreas]);

  const toggleSubArea = useCallback((subAreaId) => {
    setSubAreaIds((ids) =>
      ids.includes(subAreaId) ? ids.filter((id) => id !== subAreaId) : [...ids, subAreaId]
    );
  }, []);

  const advanceFromSubArea = useCallback(() => {
    if (subAreaIds.length === 0) return;
    setStep(2);
  }, [subAreaIds.length]);

  // ─── Group-builder plumbing ──────────────────────────────────────────────

  // IDI-327 (2026-09-13): this object was previously constructed INLINE in
  // JSX (`{ subAreaId: ..., line: initialItem }`), producing a NEW object
  // reference on every render of AddItemCascade regardless of whether the
  // underlying edit target actually changed. AddItemGroups.js's INIT_FOR_EDIT
  // effect depends on this object's IDENTITY (its own comment claims
  // "unchanged reference is a no-op") -- so any unrelated parent re-render
  // (typing in a sibling field, a margin recalculation, anything) fired a
  // fresh INIT_FOR_EDIT dispatch, silently discarding whatever the user had
  // typed into the in-progress edit draft. Memoized on the actual identity-
  // relevant primitives (initialItem?.id, subAreaId, isEditing) so the
  // reference is stable across re-renders that don't change WHICH line is
  // being edited -- the effect now only re-fires when the edit target
  // itself genuinely changes.
  const initialEditItem = useMemo(() => {
    if (!isEditing || !initialItem) return undefined;
    if (editInactiveGroup) return { subAreaId: editInactiveGroup.id, line: initialItem };
    // IDI-328: an Options-area line has NO subAreaId by design (options
    // aren't organized into sub-areas -- see groupsSubAreas below, which
    // synthesizes a single group with `id: null` for any area with zero
    // real sub-areas, Options included). The old guard
    // (`!initialItem.subAreaId` -> bail) rejected EVERY options-area edit
    // outright, since subAreaId is always null/undefined for those lines.
    //
    // IDI-357 (2026-09-24): that premise does NOT hold once a tenant adds
    // sub-areas under Options. On Sample Dealer, Options has 4 (Garage
    // Insulation Package, clean work area, Interior Walls, Vac Out), so
    // `mergedSubAreasForArea.length !== 0` and groupsSubAreas does NOT
    // synthesize the `id: null` group. Returning a hardcoded null then made
    // INIT_FOR_EDIT's `groups.find(g => g.subAreaId === null)` miss, the
    // dispatch no-op, and the edit form open with Category and Product blank.
    // Saving that blank form wrote an empty line over a real one.
    //
    // Resolve the line's actual sub-area first (same resolver the non-options
    // path uses, which also heals migrated lines carrying only subAreaLabel).
    // Fall back to null ONLY when the area genuinely has no sub-areas, which
    // is the case groupsSubAreas' synthetic group covers.
    if (initialItem.areaId === 'options') {
      const resolvedOptionsSubAreaId = resolveSubAreaIdFromItem(
        initialItem,
        mergedSubAreasForArea
      );
      if (resolvedOptionsSubAreaId) {
        return { subAreaId: resolvedOptionsSubAreaId, line: initialItem };
      }
      if (mergedSubAreasForArea.length === 0) {
        // Genuine no-sub-area Options area: matches the synthetic null group.
        return { subAreaId: null, line: initialItem };
      }
      // Sub-areas exist but this line matches none of them. No group would
      // match, so bail rather than opening a blank form the user can save
      // over real data.
      return undefined;
    }
    // IDI-339: migrated lines carry `subAreaLabel` but no `subAreaId`, so the
    // old `!initialItem.subAreaId` bail returned undefined — INIT_FOR_EDIT
    // never dispatched and the edit slideover opened blank. Resolve the id
    // from the label against the tenant-aware merged sub-area list (same list
    // groupsSubAreas maps over) so INIT_FOR_EDIT's
    // `state.groups.find(g => g.subAreaId === subAreaId)` finds the group.
    // Fresh/builder-saved lines still short-circuit on their real subAreaId.
    // IDI-448: the user moved the line off an inactive/missing sub-area.
    if (editSubAreaPick) {
      const picked = mergedSubAreasForArea.find((s) => s.id === editSubAreaPick);
      return {
        subAreaId: editSubAreaPick,
        line: { ...initialItem, subAreaId: editSubAreaPick, subAreaLabel: picked?.label || initialItem.subAreaLabel },
      };
    }
    const resolvedSubAreaId = resolveSubAreaIdFromItem(
      initialItem,
      subAreasForGroups
    ) || (editHiddenSubArea ? editHiddenSubArea.id : null);
    if (!resolvedSubAreaId) return undefined;
    return { subAreaId: resolvedSubAreaId, line: initialItem };
  }, [isEditing, initialItem, mergedSubAreasForArea, subAreasForGroups, editHiddenSubArea, editSubAreaPick, editInactiveGroup]);

  /**
   * Sub-areas passed to AddItemGroups. For areas WITHOUT sub-areas, we
   * synthesize a single group using the area itself as the sub-area (R15).
   */
  const groupsSubAreas = useMemo(() => {
    if (!areaId) return [];
    if (editInactiveGroup) return [{ id: editInactiveGroup.id, name: editInactiveGroup.label }];
    // GP-03: use merged sub-areas (includes tenant custom) not static AREAS.
    if (mergedSubAreasForArea.length === 0) {
      // Synthetic single group so the composite builder still renders.
      // PR-67a: read the merged label so a renamed BIDIT area or a
      // tenant custom top-level area gets the tenant's display name here.
      const area = mergedAreasForCascade.find((a) => a.id === areaId);
      return [{ id: null, name: area?.label || areaId }];
    }
    // Map selected sub-area IDs to {id, name}.
    return subAreaIds.map((id) => {
      const sa = subAreasForGroups.find((s) => s.id === id);
      return { id, name: sa?.label || id };
    });
  }, [areaId, subAreaIds, mergedSubAreasForArea, subAreasForGroups, mergedAreasForCascade, editInactiveGroup]);

  /** Build a committed-line object from a draft (called by the reducer). */
  const buildLineFromDraft = useCallback(({ subAreaId, draft }) => {
    // B5: look up the sub-area's labor config from the merged tenant data.
    const subAreaConfig = editInactiveGroup
      ? editInactiveGroup.config
      : (subAreasForGroups.find((s) => s.id === subAreaId) || null);
    // PR-67a hotfix (2026-08-22): stamp the TENANT's labels at commit-time.
    // `mergedAreasForCascade` and `mergedSubAreasForArea` already carry the
    // merged (renamed) labels from the PR-67a overlay. Passing them into
    // buildLineItem lets the committed line render the tenant's name for
    // the area/sub-area instead of the static VOCAB_SEEDS default (renamed
    // BIDIT area) or a raw UUID (tenant-created sub-area).
    //
    // Non-passing callers (older code paths, tests) still hit buildLineItem
    // with these fields undefined → falls back to the AREAS lookup → same
    // as pre-hotfix behavior. That's why this is a hotfix and not a refactor.
    const areaObj = mergedAreasForCascade.find((a) => a.id === areaId);
    const subAreaObj = subAreasForGroups.find((s) => s.id === subAreaId);
    return buildLineItem({
      areaId,
      // IDI-448: the stored-label group of a label-only line whose sub-area
      // is gone keeps "no sub-area id", exactly as the line was.
      subAreaId: subAreaId === STORED_SUB_AREA_ID ? null : subAreaId,
      // IDI-662 D2: an inactive area's line keeps its stored names.
      areaLabel: editInactiveGroup ? editInactiveGroup.areaLabel : areaObj?.label,
      subAreaLabel: editInactiveGroup ? editInactiveGroup.subAreaLabel : subAreaObj?.label,
      categoryId: draft.categoryId,
      productId: draft.productId,
      // IDI-178: quote-level Round Materials toggle applied to the entered
      // sqft before it reaches buildLineItem's cost calculation. See
      // quote-builder/roundMaterials.js for the placeholder-rule rationale.
      sqft: applyRoundMaterials(parseFloat(draft.sqft) || 0, roundMaterials),
      // IDI-173: the Coverage dropdown's choice prices the line.
      coverageChoice: draft.coverageChoice || null,
      note: draft.note,
      addlLabor: draft.addlLabor,
      addlCost: draft.addlCost,
      accessories: draft.accessories || [],
      alternatives: [],
      // PR-62 §3aj: alias thread-through. buildLineItem drops it from the
      // output when empty (see seedCatalog.js), so a blank input never
      // pollutes the line with a stray `alias: ''`.
      alias: draft.alias || '',
      tenantCategoryMargins,
      subAreaLaborConfig: subAreaConfig,
      tenantLaborDefaults,
      // PR-66 s2c (Q-11): tenant Quote Items so buildLineItem can resolve
      // productIds that aren't in the dealer's catalog (IDI-136: no built-ins).
      tenantProducts: tenantQuoteItems,
    });
  // mergedSubAreasForArea is kept (subAreasForGroups derives from it) so the
  // IDI-178 deps-array pin still matches.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId, tenantCategoryMargins, tenantLaborDefaults, tenantQuoteItems, mergedSubAreasForArea, mergedAreasForCascade, subAreasForGroups, roundMaterials, editInactiveGroup]);

  /**
   * Commit handler: iterate groups → emit one onAddItem call per line so
   * QuoteBuilderPage.handleAddItem writes them as distinct rows. Distinct
   * IDs are already set by buildLineItem() (timestamp+random suffix).
   */
  const commitGroups = useCallback((groups) => {
    if (!Array.isArray(groups)) return;
    // PR-62 (2026-08-19, replaces PT-15 #2 optionMeta stamping):
    //   Options area → tag every emitted line with an `optionId` so
    //   AreaCard groups them into one Option card. Which optionId depends
    //   on the user's Group selection this commit:
    //     '__new__'       → mint a fresh id (a new group appears)
    //     existing id     → reuse (lines join an existing Option N)
    //   `optionName`, `optionDescription`, group-level `optionMargin`
    //   are NEVER stamped. Name is derived at render (§3au / N-2),
    //   description doesn't exist (361_ §1 Q1), margin is per-line (§3ak).
    //   Per-line margin (`optionLineMargin`) is applied as the line's own
    //   `margin` when set — same field regular items use.
    const isOptionArea = areaId === 'options';
    let optionId = null;
    if (isOptionArea) {
      optionId =
        selectedOptionGroupId && selectedOptionGroupId !== '__new__'
          ? selectedOptionGroupId
          : `opt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
    const parsedMargin =
      isOptionArea && optionLineMargin !== '' && optionLineMargin != null
        ? clampMarginPct(Number(optionLineMargin) / 100)
        : null;
    // IDI-365: in edit mode exactly ONE row is being replaced. Edit mode seeds
    // a single sub-area (setSubAreaIds([editSubAreaId])), so one line is the
    // normal case -- but if the wizard ever emits more, only the first may
    // claim initialItem.id. The rest are genuinely new lines and must be added,
    // not written over the row being edited.
    let editTargetClaimed = false;
    groups.forEach((g) => {
      g.lines.forEach((line) => {
        let tagged = line;
        if (isOptionArea) {
          tagged = { ...tagged, optionId };
          if (parsedMargin != null && !Number.isNaN(parsedMargin)) {
            // PR-62 (367_ §1): the margin engine reads `lineMargin` as the
            // Level-1 per-line override (marginEngine.js:325). The pre-existing
            // OptionForm writer (applyOptionEdit) stamped `margin` — a key the
            // engine ignores. We stamp the CORRECT key so option lines actually
            // pick up their custom margin per §3ak / §3ao precedence chain.
            tagged.lineMargin = parsedMargin;
          }
        }
        // IDI-365: preserve the ORIGINAL line id on edit.
        //
        // The comment previously here claimed "the line's id is preserved by
        // the reducer's SAVE_ITEM edit-in-place branch, so tagged.id === the
        // original initialItem.id". That is false: SAVE_ITEM is never
        // dispatched in production (AddItemGroups dispatches INIT_GROUPS /
        // ACTIVATE_GROUP / UPDATE_DRAFT / START_EDIT / CANCEL_EDIT /
        // DELETE_LINE / ADD_ALTERNATIVE only). Lines reach here via
        // commitReady(), which pushes buildLine(...) output directly and
        // carries no _editingLineId -- so buildLineItem minted a FRESH
        // `li-${Date.now()}-...` id (seedCatalog.js:831).
        //
        // handleEditItem then ran `prev.map(li => li.id === itemId ...)`
        // against an id that exists nowhere in lineItems, so the edit was
        // silently dropped. That is the "line edits don't stick" defect.
        //
        // Re-stamp initialItem.id so the parent's merger finds the row.
        if (isEditing && onEditItem && tagged && initialItem && initialItem.id && !editTargetClaimed) {
          editTargetClaimed = true;
          const { id: _discardedFreshId, ...updates } = tagged;
          onEditItem(initialItem.id, updates);
        } else {
          onAddItem(tagged);
        }
      });
    });
    handleClose();
  }, [areaId, selectedOptionGroupId, optionLineMargin, onAddItem, onEditItem, isEditing, initialItem, handleClose]);

  const requestAccessoryPicker = useCallback((ctx) => {
    setAccessoryCtx(ctx);
  }, []);

  const closeAccessoryPicker = useCallback(() => {
    setAccessoryCtx(null);
  }, []);

  /**
   * AccessoryPicker's onCommit hands back only the newly-selected rows.
   * We append them to the draft's existing accessories — matches
   * EditItemModal's `setAccessories((prev) => [...prev, ...rows])` pattern.
   */
  const handleAccessoryPickerCommit = useCallback((rows) => {
    if (!accessoryCtx || !groupsRef.current) return;
    const existing = accessoryCtx.accessories || [];
    const merged = [...existing, ...(rows || [])];
    groupsRef.current.applyAccessoriesToActiveDraft(accessoryCtx.subAreaId, merged);
    setAccessoryCtx(null);
  }, [accessoryCtx]);

  const requestAltModal = useCallback((ctx) => {
    setAltCtx(ctx);
  }, []);

  const closeAltModal = useCallback(() => {
    setAltCtx(null);
  }, []);

  const commitAlternative = useCallback((alternative) => {
    if (altCtx && groupsRef.current) {
      groupsRef.current.applyAlternativeToLine(
        altCtx.subAreaId,
        altCtx.lineId,
        alternative
      );
    }
    setAltCtx(null);
  }, [altCtx]);

  // ─── Custom-item single-line branch ──────────────────────────────────────

  // PR-06 FIX (2026-07-22): commit to ONE sub-area only (no fan-out). Choose
  // in this order: (1) the explicit `customData.subAreaId` set via the picker
  // inside the custom form, (2) the FIRST selected sub-area if any,
  // (3) `null` for areas without sub-areas (Options).
  const submitCustomItem = useCallback(() => {
    // IDI-420: never commit an unrepresentable margin, even if a caller
    // bypasses the disabled button (Enter key, programmatic submit).
    if (customMarginError) return;
    const inactiveSubId = editInactiveGroup && editInactiveGroup.id !== STORED_SUB_AREA_ID ? editInactiveGroup.id : null;
    const chosenSubAreaId = editInactiveGroup
      ? inactiveSubId
      : (customData.subAreaId ?? (subAreaIds.length ? subAreaIds[0] : null));
    // PR-67a hotfix twin (§1.17): custom-item flow must also stamp merged
    // labels. Fixing buildLineItem + its caller and leaving the custom-item
    // path stamping from static AREAS would ship the exact bug this hotfix
    // exists to fix, on a parallel code path.
    const areaObj = mergedAreasForCascade.find((a) => a.id === areaId);
    const subAreaObj = mergedSubAreasForArea.find((s) => s.id === chosenSubAreaId);
    const item = buildCustomLineItem({
      areaId,
      subAreaId: chosenSubAreaId,
      areaLabel: editInactiveGroup ? editInactiveGroup.areaLabel : areaObj?.label,
      subAreaLabel: editInactiveGroup ? editInactiveGroup.subAreaLabel : subAreaObj?.label,
      description: customData.description,
      materialCost: Math.round(parseFloat(customData.materialCost || 0) * 100),
      laborCost: Math.round(parseFloat(customData.laborCost || 0) * 100),
      margin: parseNullableMarginPct(customData.margin),
      // PR-62 §3aj: alias thread-through to the customer document label chain.
      alias: customData.alias || '',
    });
    // PR-62 (2026-08-19, replaces PT-15 #2 optionMeta stamping):
    //   Same rules as commitGroups — tag with optionId only when in Options
    //   area; never stamp optionName / optionDescription / group-margin;
    //   apply optionLineMargin (if set) as the line's own margin.
    const isOptionArea = areaId === 'options';
    let tagged = item;
    if (isOptionArea) {
      const optionId =
        selectedOptionGroupId && selectedOptionGroupId !== '__new__'
          ? selectedOptionGroupId
          : `opt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      tagged = { ...item, optionId };
      if (optionLineMargin !== '' && optionLineMargin != null) {
        const parsed = clampMarginPct(Number(optionLineMargin) / 100);
        if (!Number.isNaN(parsed)) {
          // PR-62 (367_ §1): stamp `lineMargin` — the key the margin engine
          // reads. See commitGroups above for the full explanation. When in
          // Options area, the group panel's optionLineMargin overrides any
          // value the custom-item form's Margin % input set via buildCustomLineItem.
          tagged.lineMargin = parsed;
        }
      }
    }
    // IDI-176: same edit-mode routing as commitGroups above. Custom items
    // are a different code path (submitCustomItem, not the AddItemGroups
    // flow), but the same commit contract applies.
    //
    // IDI-365: same fresh-id defect as commitGroups -- buildCustomLineItem
    // also mints `li-${Date.now()}-...` (seedCatalog.js:944), so routing on
    // tagged.id targeted a row that does not exist and the edit was dropped.
    // Route on initialItem.id.
    if (isEditing && onEditItem && tagged && initialItem && initialItem.id) {
      const { id: _discardedFreshId, ...updates } = tagged;
      onEditItem(initialItem.id, updates);
    } else {
      onAddItem(tagged);
    }
    handleClose();
  }, [areaId, subAreaIds, customData, customMarginError, selectedOptionGroupId, optionLineMargin, onAddItem, onEditItem, isEditing, initialItem, handleClose, mergedAreasForCascade, mergedSubAreasForArea, editInactiveGroup]);

  const canSubmitCustom =
    customData.description.trim() && (customData.materialCost || customData.laborCost) && !customMarginError;

  // ─── Rendering helpers ───────────────────────────────────────────────────

  const renderAreaStep = () => (
    <div>
      <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
        Select the area where these items will be installed.
      </p>
      {/* PR-45 (2026-08-18, 339_ §1): the filter that excluded `options`
          from this grid is gone. Options is a first-class area in the
          vocabulary (vocabSeeds.js:390). IDI-334 (2026-09-12) correction: the
          previous version of this comment claimed Step 1 → Step 2 → Step 3
          "gives it sub-areas for free via mergeAreasWithTenant" — checked
          against the actual code and that's not what happens.
          mergeAreasWithTenant has no special-case for 'options'; it stays at
          zero merged sub-areas for virtually every tenant. What actually
          keeps it reachable is `selectArea` (below) jumping straight to the
          step-2 composite builder when an area has zero sub-areas, and
          `groupsSubAreas` synthesizing a single group using the area itself
          as the sub-area (R15) — Options never needs sub-areas at all. This
          is also why it's exempted from IDI-334's hide-when-empty filter
          (visibleAreasForCascade, above): it can't be "restored by tenant
          config" the way Basement/Air Sealing/Vent/Removal can, because
          nothing in this flow ever writes a sub-area under it.
          The "Create an Option" affordance below now duplicates the grid
          entry (both call selectArea('options') at :148); left in place as
          a visual shortcut per 339_ §1 second bullet.

          PR-10 grid geometry preserved: 2-col grid per
          QB-W-add-item-wizard-STATES.html v1.1 .cards { 1fr 1fr }. */}
      <div className="grid grid-cols-2 gap-2">
        {visibleAreasForCascade.map((area) => (
          <button
            key={area.id}
            type="button"
            onClick={() => selectArea(area.id)}
            className="w-full text-left px-4 py-3 rounded-md border border-[color:var(--color-border)] transition-colors hover:border-red-200"
            style={{
              borderColor: areaId === area.id ? 'var(--red-400)' : 'var(--color-border)',
              backgroundColor: areaId === area.id ? 'var(--red-50)' : 'var(--color-surface)',
            }}
          >
            <span className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>
              {area.label}
            </span>
            {/* IDI-176 E5 (2026-09-05): when the tenant has renamed a canonical
                BIDIT area (e.g., "Options" → "Options For Bids"), the merge
                stamps `originalLabel` on the area row. Surfacing it here
                disambiguates the picker without touching layout — users can
                still identify the canonical area by its original name.
                Absent originalLabel (or when it matches label), no extra text
                renders. Also disambiguates two tenant-custom areas that
                happen to share a label with a seed (same-label case) when
                the seed's originalLabel differs. See areasModel.js:mergeAreasWithTenant. */}
            {area.originalLabel && area.originalLabel !== area.label && (
              <span className="ml-2 text-xs italic" style={{ color: 'var(--color-text-secondary)' }}>
                (renamed from {area.originalLabel})
              </span>
            )}
            {/* §6.3 (2026-07-29): show sub-area count on ALL area buttons for
                consistency. Prior render hid the label when count === 0, which
                made 3 of 7 areas look different from the other 4. Zero is a
                real (and useful) piece of information. */}
            <span className="ml-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {area.subAreas.length} sub-area{area.subAreas.length === 1 ? '' : 's'}
            </span>
          </button>
        ))}
      </div>

      {/* PR-65 Q-15 (2026-08-19): the "Create an Option" card was removed.
          Options remain reachable as an AREA (N-3) — the area grid above
          renders 'options' alongside the seeded areas. This card was a
          duplicate entry point with different visual treatment; the operator
          batch called for removing it. `areaId: 'options'` is still a valid
          vocab id; existing option lines render unchanged. */}
    </div>
  );

  const renderSubAreaStep = () => {
    // PR-67a: five-state fork. LOADING / EMPTY / ERROR / NOT_PERMITTED
    // each get a distinct render. Previously all four collapsed into an
    // empty list rendered as blank cards.
    //
    // POPULATED is the normal path: merged BIDIT + tenant sub-areas for
    // the current area, rendered as a multi-select; tenant sub-areas carry
    // a subtle "Custom" chip so the user knows which ones came from
    // Settings vs. from BIDIT.
    if (subAreasFetch.kind === 'LOADING') {
      return (
        <div>
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            Loading sub-areas…
          </p>
          <div className="grid grid-cols-2 gap-2" aria-busy="true">
            {[0, 1, 2, 3].map((k) => (
              <div
                key={k}
                className="w-full px-4 py-3 rounded-md border border-[color:var(--color-border)] animate-pulse"
                style={{ backgroundColor: 'var(--color-surface)', minHeight: '3rem' }}
              />
            ))}
          </div>
        </div>
      );
    }
    if (subAreasFetch.kind === 'ERROR') {
      return (
        <div>
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            Couldn’t load sub-areas.
          </p>
          <div className="mt-4 flex justify-start">
            <Button size="sm" onClick={retrySubAreasFetch}>Retry</Button>
          </div>
        </div>
      );
    }
    if (subAreasFetch.kind === 'NOT_PERMITTED') {
      return (
        <div>
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            You don’t have permission to view sub-areas.
          </p>
        </div>
      );
    }
    // EMPTY (tenant added no rows) still shows the BIDIT seed sub-areas if
    // any exist for this area — that's what `mergedSubAreasForArea` handles
    // via mergeAreasWithTenant. When the merged set is truly empty (rare,
    // e.g. Options area), fall through to the guidance message.
    const subAreas = mergedSubAreasForArea;
    if (subAreas.length === 0) {
      return (
        <div>
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            No sub-areas configured. Go to Settings → Quote Areas to add one.
          </p>
        </div>
      );
    }
    return (
      <div>
        <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
          Select one or more sub-areas. Each will get its own group in the next step.
          {subAreaIds.length > 0 && (
            <span className="ml-2 font-medium" style={{ color: 'var(--color-action-primary)' }}>
              {subAreaIds.length} selected
            </span>
          )}
        </p>
        {/* PR-10: 2-col grid to match step-0 area grid (wire .cards { 1fr 1fr }). */}
        <div className="grid grid-cols-2 gap-2">
          {subAreas.map((sa) => {
            const isSelected = subAreaIds.includes(sa.id);
            const isTenant = sa.source === 'tenant';
            return (
              <button
                key={sa.id}
                type="button"
                onClick={() => toggleSubArea(sa.id)}
                className="w-full text-left px-4 py-3 rounded-md border border-[color:var(--color-border)] transition-colors hover:border-red-200 flex items-center justify-between gap-2"
                style={{
                  borderColor: isSelected ? 'var(--red-400)' : 'var(--color-border)',
                  backgroundColor: isSelected ? 'var(--red-50)' : 'var(--color-surface)',
                }}
                aria-pressed={isSelected}
              >
                <span className="flex items-center gap-2 text-sm min-w-0" style={{ color: 'var(--color-text-primary)' }}>
                  <span className="truncate">{sa.label}</span>
                  {isTenant && (
                    <span
                      className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700 flex-shrink-0"
                      title="Custom sub-area from Settings → Quote Areas"
                    >
                      Custom
                    </span>
                  )}
                </span>
                {isSelected && (
                  <span aria-hidden="true" className="flex-shrink-0" style={{ color: 'var(--color-action-primary)', fontWeight: 700 }}>✓</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={advanceFromSubArea} disabled={subAreaIds.length === 0}>
            Continue →
          </Button>
        </div>
      </div>
    );
  };

  const renderOptionGroupControls = () => {
    // PR-62 (2026-08-19): renders the Group selector + per-line Margin %
    // controls when adding items under the Options area. Returns null in
    // every other area so the composite / custom-item body renders unchanged.
    if (areaId !== 'options') return null;

    return (
      <div
        className="mb-4 p-3 rounded-md border"
        style={{
          backgroundColor: 'var(--color-surface-elevated, transparent)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="flex flex-wrap items-end gap-3">
          {/* PR-65 Q-04 (2026-08-19): the "Group" droplist was removed.
              `selectedOptionGroupId` state stays defaulted at '__new__' so
              the commit handlers (lines below) still produce a fresh
              option-group id for every option added via this sidesheet.
              Grouping under an existing option is deferred to a future PR
              (or the OptionForm inline surface, which retains it).
              The Gross Margin % field is unaffected — it remains the
              per-line override per §3ak / §3ao. */}
          <div className="w-[140px]">
            <Input
              label="Gross Margin %"
              type="number"
              min="0"
              max="99"
              value={optionLineMargin}
              onChange={(e) => setOptionLineMargin(e.target.value)}
              placeholder="e.g. 40"
              error={optionMarginError}
            />
          </div>
        </div>
      </div>
    );
  };

  // IDI-448: Edit on a line whose sub-area was made inactive, or no longer
  // exists, opens filled in under its stored name, says so, and offers the
  // sub-area picker to move it to another. Never an error.
  const renderEditSubAreaNotice = () => {
    if (editInactiveGroup) {
      return (
        <div className="mb-4" data-testid="edit-area-notice">
          <Alert variant="warning">
            <p className="text-sm">{`The area "${editInactiveGroup.areaLabel}" is inactive. This line keeps its name.`}</p>
          </Alert>
        </div>
      );
    }
    if (!editSubAreaNotice || editSubAreaPick) return null;
    const name = editSubAreaNotice.label;
    const message = editSubAreaNotice.editStatus === 'inactive'
      ? `The sub-area "${name}" is inactive. This line keeps its name; pick another sub-area to move it.`
      : `The sub-area "${name}" no longer exists. This line keeps its name; pick another sub-area for it.`;
    const choices = mergedSubAreasForArea.map((s) => ({ value: s.id, label: s.label }));
    return (
      <div className="mb-4" data-testid="edit-subarea-notice">
        <Alert variant="warning">
          <p className="text-sm mb-2">{message}</p>
          {choices.length > 0 && (
            <SelectMenu
              size="sm"
              width="full"
              aria-label="Pick another sub-area"
              placeholder="Pick another sub-area…"
              value={null}
              onChange={pickEditSubArea}
              options={choices}
            />
          )}
        </Alert>
      </div>
    );
  };

  const renderCompositeStep = () => {
    // PR-62 (2026-08-19, 361_ §1 Q2 + N-3): the OptionForm interstitial is
    // gone. The Options area now falls through to the composite / custom-item
    // step like every other area. Option-group affiliation and per-line
    // margin are collected via the Group selector rendered inline below
    // (see renderOptionGroupControls). Line-level Alias lives on every item
    // form (composite: ExtrasAccordion in AddItemGroups; custom: inline below).
    if (isCustomItem) {
      return (
        <div className="space-y-4">
          {/* PR-62: Group selector + per-line margin above the custom-item body,
              same treatment as the composite path. Renders null when not in
              the Options area. */}
          {renderOptionGroupControls()}
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            Enter custom item details. Cost and measure fields are editable.
          </p>
          <Input
            label="Description"
            value={customData.description}
            onChange={(e) => setCustomData((d) => ({ ...d, description: e.target.value }))}
            placeholder="Custom item description…"
          />
          {/* PR-06 FIX: sub-area picker when >1 sub-areas were selected.
              Prevents the previous double-add bug. Defaults to first selected. */}
          {subAreaIds.length > 1 && (
            <div>
              <label
                className="block text-xs font-medium mb-1"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Add to sub-area
              </label>
              <SelectMenu
                size="sm"
                width="full"
                value={customData.subAreaId ?? subAreaIds[0]}
                onChange={(val) => setCustomData((d) => ({ ...d, subAreaId: val }))}
                options={subAreaIds.map((sid) => {
                  // PR-67a: read from mergedSubAreasForArea so renamed BIDIT
                  // sub-areas + tenant custom sub-areas both surface the
                  // tenant's label here.
                  const sa = mergedSubAreasForArea.find((s) => s.id === sid);
                  return { value: sid, label: sa?.label || sid };
                })}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Material Cost ($)"
              type="number"
              min="0"
              step="0.01"
              value={customData.materialCost}
              onChange={(e) => setCustomData((d) => ({ ...d, materialCost: e.target.value }))}
              placeholder="0.00"
            />
            <Input
              label="Labor Cost ($)"
              type="number"
              min="0"
              step="0.01"
              value={customData.laborCost}
              onChange={(e) => setCustomData((d) => ({ ...d, laborCost: e.target.value }))}
              placeholder="0.00"
            />
          </div>
          <Input
            label="Margin %"
            type="number"
            min="0"
            max="99"
            value={customData.margin}
            onChange={(e) => setCustomData((d) => ({ ...d, margin: e.target.value }))}
            placeholder="35"
            error={customMarginError}
          />
          {/* PR-62 §3aj: Alias field on the custom-item path. Optional.
              When set, renders on customer surfaces (QuoteSelectionPanel) in
              place of the description. Never visible on internal surfaces. */}
          <Input
            label="Alias (customer document only)"
            value={customData.alias}
            onChange={(e) => setCustomData((d) => ({ ...d, alias: e.target.value }))}
            placeholder="e.g. Playroom / Storage Room"
          />
        </div>
      );
    }
    return (
      <div>
        {/* PR-62 (2026-08-19): Group selector + per-line margin for the Options
            area. Rendered inline above the composite builder — no interstitial.
            When areaId !== 'options' these are hidden. */}
        {renderOptionGroupControls()}
        {renderEditSubAreaNotice()}
        <AddItemGroups
          ref={groupsRef}
          areaId={areaId}
          subAreas={groupsSubAreas}
          buildLine={buildLineFromDraft}
          onCommit={commitGroups}
          onDirtyChange={setGroupsDirty}
          onRequestAccessoryPicker={requestAccessoryPicker}
          onRequestAltModal={allowAlternates ? requestAltModal : undefined}
          targetNoun={targetNoun}
          marginContext={marginContext}
          /* PR-66 s2c (Q-11): tenant Quote Items merged into Category /
             Product droplists inside each GroupCard. */
          tenantQuoteItems={tenantQuoteItems}
          /* IDI-176 · edit-mode seed. When set, AddItemGroups dispatches
             INIT_FOR_EDIT after its INIT_GROUPS effect, seeding the target
             sub-area group with `initialItem` and activating a prefilled
             edit draft. In add-mode this is undefined and AddItemGroups
             renders normally. See addItemGroupsState.js:INIT_FOR_EDIT for
             the reducer contract. */
          initialEditItem={initialEditItem}
        />
        <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <button
            type="button"
            onClick={() => setIsCustomItem(true)}
            className="text-sm underline"
            style={{ color: 'var(--color-action-primary)' }}
          >
            + Add a custom item instead (single-line)
          </button>
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (step) {
      case 0: return renderAreaStep();
      case 1: return renderSubAreaStep();
      case 2: return renderCompositeStep();
      default: return null;
    }
  };

  // ─── Footer ──────────────────────────────────────────────────────────────

  // IDI-365: an edit that can't be priced (product not in the loaded
  // catalog) used to throw inside commit and vanish. Surface it instead.
  const [commitError, setCommitError] = useState(null);
  const handleAddToQuote = useCallback(() => {
    if (isCustomItem) {
      if (canSubmitCustom) submitCustomItem();
      return;
    }
    if (catalogLoading) return;
    if (groupsRef.current) {
      try {
        setCommitError(null);
        groupsRef.current.commit(); // handles the "Save item first" nudge internally
      } catch (err) {
        setCommitError(
          err && err.code === 'UNRESOLVABLE_PRODUCT_ID'
            ? 'This product is no longer in your Item Catalog, so the line can\u2019t be priced. Pick a product again.'
            : (err && err.message) || 'This line could not be saved.'
        );
      }
    }
  }, [isCustomItem, canSubmitCustom, submitCustomItem, catalogLoading]);

  const backLabel = step === 0 ? null : '← Back';
  const backHandler = () => {
    // From step 2, "Back" returns to sub-area step (or area step if no sub-areas).
    if (step === 2) {
      // PR-62 (2026-08-19): the OptionForm interstitial is gone. Back from
      // step 2 always returns to the sub-area / area picker (no more
      // options-specific sub-step to unwind).
      setIsCustomItem(false); // clear custom branch on back
      setStep(hasSubAreas ? 1 : 0);
      return;
    }
    setStep(Math.max(0, step - 1));
  };

  const footer = (
    <div className="flex items-center justify-between w-full">
      <div className="flex gap-2">
        {backLabel && (
          <Button variant="secondary" size="sm" onClick={backHandler}>
            {backLabel}
          </Button>
        )}
      </div>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={handleClose}>Cancel</Button>
        {/* PR-62 (2026-08-19, replaces PT-15 #2 hide-during-OptionForm):
            the OptionForm interstitial is gone, so the normal commit button
            is always available on step 2. */}
        {step === 2 && (
          <Button
            size="sm"
            onClick={handleAddToQuote}
            disabled={(isCustomItem ? !canSubmitCustom : (catalogLoading)) || !!optionMarginError}
            title={
              (isCustomItem && customMarginError) || optionMarginError
                ? ((isCustomItem && customMarginError) || optionMarginError)
                : (!isCustomItem && catalogLoading ? 'Loading your Item Catalog\u2026'
                  : (groupsDirty && !isCustomItem ? `Save the in-progress item before adding to ${targetNoun}` : ''))
            }
          >
            {!isCustomItem && catalogLoading ? 'Loading catalog\u2026' : `Add to ${targetNoun}`}
          </Button>
        )}
      </div>
    </div>
  );

  // ─── Header labels ───────────────────────────────────────────────────────

  const stepLabels = hasSubAreas
    ? ['Area', 'Sub-areas', 'Items']
    : ['Area', 'Items'];
  const visibleStepIndex = hasSubAreas ? stepIndex : (stepIndex === 0 ? 0 : 1);
  // §6.2 (2026-07-29): consistent step label — always "Step N of M: <name>".
  // Previously the first screen (before area picked) showed just "Step 1: Area"
  // then jumped to "Step 2 of 3: Sub-areas", which read as an inconsistency.
  // On step 0 we default to the 3-step total (the majority case; if the picked
  // area has no sub-areas the total drops to 2 on the next step).
  const displayTotal = areaId ? totalSteps : 3;
  const subtitle = `Step ${visibleStepIndex + 1} of ${displayTotal}: ${stepLabels[visibleStepIndex]}`;

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <>
      <Slideover
        open={open}
        onClose={handleClose}
        title={isEditing ? "Edit Line Item" : "Add Line Item"}
        subtitle={subtitle}
        width="lg"
        footer={footer}
      >
        {/* Step indicator — 2 or 3 dots depending on subAreaFlow (R15). */}
        <div className="flex gap-1 mb-4">
          {stepLabels.map((label, i) => (
            <div
              key={label}
              className="h-1 flex-1 rounded-full transition-colors"
              style={{
                backgroundColor: i <= visibleStepIndex
                  ? 'var(--color-action-primary)'
                  : 'var(--neutral-200)',
              }}
            />
          ))}
        </div>
        {commitError && (
          <div className="mb-3">
            <Alert variant="error">{commitError}</Alert>
          </div>
        )}
        {renderStepContent()}
      </Slideover>

      {/* Sibling AccessoryPicker (opens over the wizard; both stay mounted).
          Headless UI Dialog supports stacking (matches EditItemModal pattern). */}
      {accessoryCtx && (
        <AccessoryPicker
          open={Boolean(accessoryCtx)}
          onClose={closeAccessoryPicker}
          areaId={areaId}
          areaLabel={mergedAreasForCascade.find((a) => a.id === areaId)?.label}
          catalog={accessoryCatalog}
          existingIds={(accessoryCtx.accessories || []).map((a) => a.accessory_id).filter(Boolean)}
          onCommit={handleAccessoryPickerCommit}
          onCreateCustom={onCreateCustomAccessory}
        />
      )}

      {/* Sibling AddAlternateModal for a committed line inside the wizard.
         AddAlternateModal.onAdd signature is (parentId, alt); we already know
         which line the alt is for (altCtx.lineId), so we ignore parentId. */}
      {allowAlternates && altCtx && (
        <AddAlternateModal
          open={Boolean(altCtx)}
          onClose={closeAltModal}
          onAdd={(_parentId, alternative) => commitAlternative(alternative)}
          parentItem={altCtx.parentLine}
          tenantCategoryMargins={tenantCategoryMargins}
          tenantQuoteItems={tenantQuoteItems}
        />
      )}
    </>
  );
}
